/**
 * Central registry of premium hero images per service.
 * Every image supplied by the Kitxens team, never AI-generated or Unsplash.
 *
 * IMAGE INTEGRATION RULES (source of truth):
 *   - One unique image per page. No reuse across service pages.
 *   - Rendering is standardized by <HeroImage> — fixed aspect-[4/3], object-cover.
 *     The container never changes; only the src/alt swap between pages.
 *   - `alt` is mandatory for a11y + SEO.
 *
 * Adding a new hero:
 *   1. Upload the file to the customer-assets bucket.
 *   2. Add an entry keyed by the service slug with { src, alt }.
 *   3. SubPage.jsx, ServiceLandingTemplate.jsx and flagship pages pick it up
 *      automatically via heroFor(slug).
 */

export const SERVICE_HERO_IMAGES = {
  // ── IT & POS Systems (SubPage on /it-pos-systems) — master reference ─
  'it-pos-systems': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/g2xqqxmv_IT%20POS%20SYSTEMS%20httpskitxens.comit-pos-systems.png',
    alt: 'Modern restaurant point-of-sale terminal with dashboard, thermal printer and card reader on a marble counter, warm restaurant ambience behind.',
  },
  // ── POS Management (/services/pos-management) — updated batch ─────
  'pos-management': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/dw5ud6hi_POS%20Management%20httpskitxens.comservicespos-management.png',
    alt: 'Cloud-based restaurant POS terminal on a marble counter — dark dashboard with sales overview, orders, top-selling items and connected integrations (Toast, Uber Eats, DoorDash) — flanked by a thermal printer and payment reader.',
  },
  // ── POS Integrations (/services/pos-integrations) ─────────────────
  'pos-integrations': {
    src: '/images/pos-integrations-hero.png',
    alt: 'Restaurant POS tablet showing an Integrations dashboard with 12 connected apps — Toast, Clover, Square, Google Business Profile, Uber Eats, DoorDash, Grubhub, Olo, ezCater, Menufy, Orders2Me and IDA Inside Delivery — connected by luminous data lines across a warm restaurant scene.',
  },
  // ── Online Ordering (flagship /services/online-ordering) ──────────
  'online-ordering': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/pnyifnlw_Delivery.png',
    alt: 'Mobile-friendly restaurant menu on a smartphone with dish photography, prices and a prominent Order Now button — direct online ordering flow.',
  },
  // ── Restaurant Websites (flagship /services/restaurant-websites) ──
  'restaurant-websites': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/9r8hragm_imagen%20hero%20dise%C3%B1o%20kitxens.png',
    alt: 'Restaurant website displayed across laptop, tablet and phone in a warm, sunlit setting — responsive design crafted by Kitxens.',
  },
  // ── Local SEO (/services/local-seo) ───────────────────────────────
  'local-seo': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/4sk5c4uf_ChatGPT%20Image%20Jul%201%2C%202026%2C%2003_13_30%20PM.png',
    alt: 'Tablet showing a local map with nearby restaurants — cuisine, star ratings and distance — the surface a diner actually uses to choose where to eat.',
  },
  // ── Google Business Profile (/services/google-business-profile) ───
  'google-business-profile': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/segteywv_ChatGPT%20Image%20Jul%201%2C%202026%2C%2003_17_51%20PM.png',
    alt: 'Bella Napoli Google Business Profile insights dashboard on a tablet — views, searches, orders, calls, popular times and customer actions.',
  },
  // ── Online Reputation (/services/online-reputation) ───────────────
  'online-reputation': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/3aj4kjwq_ChatGPT%20Image%20Jul%201%2C%202026%2C%2003_21_05%20PM.png',
    alt: 'Smartphone review feed showing a 4.8-star average across 1,200+ recent reviews, positive comments about food, service and atmosphere.',
  },
  // ── Digital Branding (flagship /digital-branding) ─ 16:9 hero ────
  'digital-branding': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/hqu94k1z_primera%20imagen%20%28hero%29.png',
    alt: 'Luxury creative workspace on an oak desk — MacBook showing a modern restaurant website, iPhone with a premium Instagram feed, iPad with Google Business Profile, printed menus, moodboards, color palettes, packaging and camera — Scandinavian studio with natural daylight.',
  },
  // ── Delivery Optimization (/services/delivery-optimization) ───────
  'delivery-optimization': {
    src: 'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/pnyifnlw_Delivery.png',
    alt: 'Hand holding a smartphone with a restaurant delivery ordering app — optimized menus, photos and rankings across third-party delivery platforms.',
  },
};

/** Utility for consumers. */
export function heroFor(slug) {
  return SERVICE_HERO_IMAGES[slug] || null;
}
